stimulusApp.register(
  "filters-form",
  class extends Stimulus.Controller {
    static targets = ["stayLoading", "boundingBox"]
    static values = { boundingBox: String }

    static settingsHighlightClass = "fltr--option-toggle_readonly-highlight"

    static stayScopeParams = ["stay_filters", "sidebar_only"]

    #countsBoundingBox
    #countsRefresh

    submit(event) {
      if (this.#refuseLastOption(event.target)) return

      this.#capture(event.target)
      this.#revealSettings(event.target)
      const restore = this.#resolveSettingsParams()
      this.element.requestSubmit()
      restore()
    }

    applyFilter(input) {
      input.checked = true
      this.#revealSettings(input)
      const restore = this.#resolveSettingsParams()
      this.element.requestSubmit()
      restore()
    }

    loadStayCounts(event) {
      if (!event.target.open) return

      this.#requestStayCounts()
    }

    // Either the section the user just expanded is waiting on the counts, or the
    // render itself never paid for counts its URL implies — the marker carries
    // the second case, because the stay sections are collapsed there and the
    // wrong count line and missing price chip are outside this element.
    stayLoadingTargetConnected(element) {
      if (!element.dataset.stayHeal && !this.#stayCountsOpen) return

      this.#requestStayCounts()
    }

    scopeCounts(boundingBox) {
      this.#countsBoundingBox = boundingBox ?? ""
      this.#refreshCounts()
    }

    boundingBoxValueChanged() {
      this.#refreshCounts()
    }

    supersedeCountsRefresh({ target }) {
      if (
        new URL(target.action).pathname !==
        new URL(this.element.action).pathname
      ) {
        return
      }

      this.#countsRefresh?.abort()
    }

    // The event name follows the resulting state, and the payload is the row's
    // rendered vocabulary. The budget slider reaches `submit` through its own
    // hidden `price` field, which carries no vocabulary and reports
    // `filter price-range` for itself.
    #capture(input) {
      const tracking = input.dataset.filterTracking
      if (!tracking) return

      Activity.capture(`filter ${input.checked ? "select" : "deselect"}`, {
        filter: JSON.parse(tracking),
      })
    }

    // Absent means closed, the same spelling the server uses: `morph-keep` lets a
    // removal through and blocks an update, so writing `"false"` here would leave
    // the row unable to reset when the filter is unchecked.
    #revealSettings(input) {
      const row = input.closest(".fltr--container")
      if (!row?.querySelector(":scope > .fltr--settings")) return

      if (input.checked) {
        row.dataset.open = "true"
      } else {
        delete row.dataset.open
      }
    }

    // A setting group never empties out: the click leaves the state alone and the
    // toggle shakes.
    #refuseLastOption(input) {
      const group = input.closest(".fltr--setting")
      if (!group || input.checked) return false
      if (group.querySelector("input[type='checkbox']:checked")) return false

      input.checked = true
      this.#shake(input.parentElement.querySelector(".fltr--option-toggle"))
      return true
    }

    #shake(toggle) {
      if (!toggle) return

      toggle.classList.remove(this.constructor.settingsHighlightClass)
      void toggle.offsetWidth // restart the animation on a repeated click
      toggle.classList.add(this.constructor.settingsHighlightClass)
    }

    // A filter with settings travels either as fully checked or as the nested
    // per-option lists, never both, and the two narrow the search differently
    // (`apply_umrah_filters` joins the filter's own places for one and the
    // options' for the other). A
    // fully checked filter therefore travels on its parent field alone, and an
    // unchecked one has to drop its options too, since those would keep it
    // applied on their own.
    // The disabling lasts only as long as Turbo takes to build the form data — an
    // open panel would otherwise sit greyed out for the whole round trip.
    #resolveSettingsParams() {
      const withheld = []
      const withhold = (input) => {
        input.disabled = true
        withheld.push(input)
      }

      for (const settings of this.element.querySelectorAll(".fltr--settings")) {
        const row = settings.closest(".fltr--container")
        const parent = row.querySelector(
          ":scope > .fltr > input[type='checkbox']",
        )
        const options = Array.from(
          settings.querySelectorAll("input[type='checkbox']"),
        )

        if (!parent.checked || options.every((option) => option.checked)) {
          options.forEach(withhold)
          continue
        }

        // Leisure and other-filter options share their parent's param root, and
        // Rack raises `ParameterTypeError` on a key that arrives as both an
        // array and a hash — so the parent field has to go instead.
        const root = parent.name.replace(/\[\]$/, "")
        if (options.some((option) => option.name.startsWith(`${root}[`))) {
          withhold(parent)
        }
      }

      return () => withheld.forEach((input) => (input.disabled = false))
    }

    // The fields live only as long as it takes Turbo to build the form data:
    // `sidebar_only` left behind would make the user's next filter change
    // sidebar-only too, leaving the results and the address bar on the old search.
    #requestStayCounts() {
      if (!this.hasStayLoadingTarget) return

      const restore = this.#resolveSettingsParams()
      const fields = this.constructor.stayScopeParams.map((name) => {
        const field = document.createElement("input")
        field.type = "hidden"
        field.name = name
        field.value = "1"
        return this.element.appendChild(field)
      })

      this.element.requestSubmit()
      restore()
      fields.forEach((field) => field.remove())
    }

    #refreshCounts() {
      const boundingBox = this.#countsBoundingBox
      if (
        boundingBox === undefined ||
        boundingBox === this.boundingBoxTarget.value
      ) {
        return
      }

      this.#countsRefresh?.abort()
      this.boundingBoxTarget.value = boundingBox
      this.boundingBoxTarget.disabled = !boundingBox

      const restore = this.#resolveSettingsParams()
      const query = new FormData(this.element)
      restore()
      query.set("sidebar_only", "1")

      this.#countsRefresh = new AbortController()
      RailsRequest.get(this.element.action, {
        query,
        responseKind: "turbo-stream",
        signal: this.#countsRefresh.signal,
      }).catch((error) => {
        if (error.name !== "AbortError") throw error
      })
    }

    get #stayCountsOpen() {
      return Boolean(
        this.element.querySelector(
          'details[open][data-action*="loadStayCounts"]',
        ),
      )
    }
  },
)
